import type { ConversationOpener } from '@/lib/desktop-shell/conversation-opener';
import type { DesktopShellStore, PendingRewind } from '@/lib/desktop-shell/store';

/** How long the copy is awaited in the conversations the shell knows. */
const COPY_ARRIVAL_MS = 15_000;

/** The conversation `id` among the shell's: at once, or when the sync brings it. */
function whenConversationKnown(
  store: DesktopShellStore,
  id: string,
  timeoutMs = COPY_ARRIVAL_MS,
): Promise<void> {
  const known = () => store.getState().syncedThreads.some((thread) => thread.id === id);
  if (known()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      unsubscribe();
      reject(new Error('The new conversation did not arrive. Find it in the sidebar.'));
    }, timeoutMs);
    const unsubscribe = store.subscribe(() => {
      if (!known()) return;
      clearTimeout(timer);
      unsubscribe();
      resolve();
    });
  });
}

/**
 * "Start a new conversation from here" (design D46): the server copies the turns before the
 * edited message into a new conversation; once the shell knows it, it opens and the edited
 * message is sent there once its agent is ready (`usePendingMessageDrain`). That conversation
 * has no agent session yet, so its first turn replays
 * the copied transcript: the agent remembers it. This conversation stays as it is.
 */
export async function startConversationFromHere(input: {
  store: DesktopShellStore;
  /** The conversation the edit was made in, which stays as it is. */
  conversationId: string;
  opener: ConversationOpener | null;
  rewind: PendingRewind;
  copy: (conversationId: string, beforeMessageId: string) => Promise<{ conversationId: string }>;
}): Promise<void> {
  const { store, opener, rewind, conversationId } = input;
  const copy = await input.copy(conversationId, rewind.messageId);
  await whenConversationKnown(store, copy.conversationId);
  if (opener === null || !(await opener.openConversation(copy.conversationId)))
    throw new Error('The new conversation could not be opened.');
  // The dialog stays (and says why) until the copy is open.
  const { setConversationState } = store.getState();
  setConversationState(conversationId, { pendingRewind: null });
  setConversationState(copy.conversationId, {
    pendingSend: { kind: 'edited', message: rewind.message, ready: true },
  });
}
